/**
 * Finance / Executive Dashboard — Phase 6.
 *
 * Org-wide rollup for EAF + L3 Finance. Tiles for the totals, a 12-month
 * trend chart, a BU comparison bar chart, top overruns table, and a pending
 * Finance approvals queue.
 *
 * Responsive grid:
 *   - KPI tiles: 1 col mobile -> 2 col sm -> 4 col lg
 *   - Charts: stacked on mobile, 2-col on lg
 *   - Tables full width on all sizes
 */

import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Chart from "react-apexcharts";
import moment from "moment";
import { fetchGet } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import MetricTile from "../components/metric_tile";
import DashboardSection from "../components/dashboard_section";
import StatusBadge from "../components/status_badge";
import { FinanceDashboardData } from "../models/dashboard_models";

const formatCurrency = (n: number) =>
  "₹" + (Number(n) || 0).toLocaleString("en-IN");
const formatShort = (n: number) => {
  const v = Math.abs(n);
  if (v >= 1e7) return "₹" + (n / 1e7).toFixed(1) + "Cr";
  if (v >= 1e5) return "₹" + (n / 1e5).toFixed(1) + "L";
  if (v >= 1e3) return "₹" + (n / 1e3).toFixed(1) + "K";
  return "₹" + Math.round(n);
};

const FinanceDashboardScreen: FunctionComponent = () => {
  const navigation = useHistory();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FinanceDashboardData | null>(null);

  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/dashboard/finance`;

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchGet(url);
      if (res.success) setData(res.data);
      else showToast({ type: ToastType.error, text: res.error });
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  const orgTrendOptions = useMemo(
    () =>
      ({
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "Nunito, sans-serif",
        },
        colors: ["#94a3b8", "#00BEA5"],
        stroke: { curve: "smooth", width: [2, 3] },
        dataLabels: { enabled: false },
        legend: { position: "top", horizontalAlign: "right" },
        xaxis: {
          categories: (data?.monthly_org_trend || []).map((m) =>
            moment(m.month + "-01").format("MMM YY"),
          ),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            formatter: (v: number) => formatShort(v),
            style: { fontSize: "11px" },
          },
        },
        tooltip: { y: { formatter: (v: number) => formatCurrency(v) } },
        grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      }) as any,
    [data],
  );

  const orgTrendSeries = useMemo(() => {
    if (!data) return [];
    return [
      {
        name: "Estimate",
        data: data.monthly_org_trend.map((m) => Math.round(m.estimate)),
      },
      {
        name: "Actual",
        data: data.monthly_org_trend.map((m) => Math.round(m.actual)),
      },
    ];
  }, [data]);

  const buBarOptions = useMemo(
    () =>
      ({
        chart: {
          toolbar: { show: false },
          fontFamily: "Nunito, sans-serif",
          stacked: false,
        },
        colors: ["#94a3b8", "#00BEA5"],
        plotOptions: {
          bar: { columnWidth: "55%", borderRadius: 4 },
        },
        dataLabels: { enabled: false },
        legend: { position: "top", horizontalAlign: "right" },
        xaxis: {
          categories: (data?.by_bu || []).map((b) => b.name),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            formatter: (v: number) => formatShort(v),
            style: { fontSize: "11px" },
          },
        },
        tooltip: { y: { formatter: (v: number) => formatCurrency(v) } },
        grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
      }) as any,
    [data],
  );

  const buBarSeries = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Estimate", data: data.by_bu.map((b) => Math.round(b.mtd_estimate)) },
      { name: "Actual", data: data.by_bu.map((b) => Math.round(b.mtd_actual)) },
    ];
  }, [data]);

  if (loading && !data) return <LoadingWidget />;
  if (!data)
    return (
      <div className="p-5 text-sm text-gray-500">No data available.</div>
    );

  const totalUtil =
    data.org.total_mtd_estimate > 0
      ? (data.org.total_mtd_actual / data.org.total_mtd_estimate) * 100
      : 0;

  return (
    <div className="p-3 sm:p-5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            Finance Dashboard
          </h1>
          <p className="text-xs text-gray-500">
            Org-wide rollup &middot; Month{" "}
            <b>{data.month}</b>{" "}
            <span className="text-gray-300">·</span> FY <b>{data.fy}</b>
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs border rounded-md px-3 py-1 hover:bg-gray-50 self-start md:self-auto"
        >
          Refresh
        </button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricTile
          title="Annual Plan"
          value={formatShort(data.org.total_annual_estimate)}
          subtitle={`${data.org.bu_count} BUs · ${data.org.dept_count} Depts`}
          accent="primary"
        />
        <MetricTile
          title="YTD Actual"
          value={formatShort(data.org.total_ytd_actual)}
          subtitle="Finance-approved since Apr"
          accent="blue"
        />
        <MetricTile
          title="MTD Actual"
          value={formatShort(data.org.total_mtd_actual)}
          subtitle={`vs est ${formatShort(data.org.total_mtd_estimate)}`}
          accent={totalUtil > 100 ? "rose" : "green"}
          trend={
            data.org.total_mtd_estimate > 0
              ? {
                  value: totalUtil - 100,
                  suffix: "% vs est",
                  invertColors: true,
                }
              : undefined
          }
        />
        <MetricTile
          title="Pending Finance"
          value={data.org.pending_finance_count}
          subtitle="Projects awaiting tier approval"
          accent="amber"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection
          title="12-month trend"
          subtitle="Org-wide estimate vs actual"
        >
          <Chart
            options={orgTrendOptions}
            series={orgTrendSeries}
            type="area"
            height={280}
          />
        </DashboardSection>

        <DashboardSection
          title="MTD by Business Unit"
          subtitle="Slice estimate vs actual for the current month"
        >
          {data.by_bu.length === 0 ? (
            <p className="text-xs text-gray-400 py-10 text-center">
              No active Business Units configured yet.
            </p>
          ) : (
            <Chart
              options={buBarOptions}
              series={buBarSeries}
              type="bar"
              height={280}
            />
          )}
        </DashboardSection>
      </div>

      {/* Tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardSection
          title="Top overruns"
          subtitle="MTD actual exceeds monthly estimate"
        >
          {data.top_overruns.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              No overruns this month — nice.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1.5">CC</th>
                    <th className="text-right font-medium py-1.5">Estimate</th>
                    <th className="text-right font-medium py-1.5">Actual</th>
                    <th className="text-right font-medium py-1.5">Over</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_overruns.map((r) => (
                    <tr
                      key={r.cc_id}
                      className="border-t cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        navigation.push(`${urls.cc_dashboard}/${r.cc_id}`)
                      }
                    >
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-800">
                          {r.cc_name}
                        </div>
                        <div className="text-gray-400 text-[10px]">
                          {r.bu_name} · {r.dept_name}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatShort(r.mtd_estimate)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatShort(r.mtd_actual)}
                      </td>
                      <td className="py-2 text-right tabular-nums font-semibold text-rose-700">
                        +{r.overrun_pct.toFixed(0)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardSection>

        <DashboardSection
          title="Pending Finance approvals"
          subtitle={
            data.pending_finance_approvals.length === 0
              ? "Queue is empty"
              : `${data.pending_finance_approvals.length} item(s)`
          }
        >
          {data.pending_finance_approvals.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">
              Nothing waiting.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-1.5">Project</th>
                    <th className="text-right font-medium py-1.5">Budget</th>
                    <th className="text-left font-medium py-1.5">Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pending_finance_approvals.map((p: any) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-800">
                          {p.name}
                        </div>
                        <div className="text-gray-400 text-[10px]">
                          {p.business_unit_name} · {p.department_name}
                        </div>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatShort(p.budget)}
                      </td>
                      <td className="py-2 pl-2">
                        <StatusBadge status={p.current_approval_level} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DashboardSection>
      </div>

      {/* BU detail table — small at the bottom */}
      <DashboardSection title="Business Units" subtitle="MTD slice vs actual and YTD actual">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left font-medium py-1.5">Name</th>
                <th className="text-right font-medium py-1.5">CCs</th>
                <th className="text-right font-medium py-1.5">Annual</th>
                <th className="text-right font-medium py-1.5">MTD est</th>
                <th className="text-right font-medium py-1.5">MTD actual</th>
                <th className="text-right font-medium py-1.5">YTD actual</th>
              </tr>
            </thead>
            <tbody>
              {data.by_bu.map((b) => (
                <tr
                  key={b.id}
                  className="border-t cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    navigation.push(`${urls.bu_dashboard}/${b.id}`)
                  }
                >
                  <td className="py-2 pr-2">
                    <div className="font-medium text-gray-800">{b.name}</div>
                    <div className="text-gray-400 font-mono text-[10px]">
                      {b.id}
                    </div>
                  </td>
                  <td className="py-2 text-right tabular-nums">{b.cc_count}</td>
                  <td className="py-2 text-right tabular-nums">
                    {formatShort(b.annual_estimate)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatShort(b.mtd_estimate)}
                  </td>
                  <td
                    className={`py-2 text-right tabular-nums ${
                      b.mtd_actual > b.mtd_estimate ? "text-rose-700" : ""
                    }`}
                  >
                    {formatShort(b.mtd_actual)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatShort(b.ytd_actual)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardSection>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default FinanceDashboardScreen;
